/**
 * Shared primitives used across API contracts.
 */
// ---------------------------------------------------------------------------
// Locale and bilingual content
// ---------------------------------------------------------------------------
export const LOCALES = {
    AR: 'ar',
    EN: 'en',
};
export const ALL_LOCALES = Object.freeze(Object.values(LOCALES));
/** Default tenant locale. Configurable per tenant. */
export const DEFAULT_LOCALE = LOCALES.AR;
export function isLocale(value) {
    return ALL_LOCALES.includes(value);
}
/** Text direction for a locale. Arabic is RTL; English is LTR. */
export function directionForLocale(locale) {
    return locale === LOCALES.AR ? 'rtl' : 'ltr';
}
/**
 * Pick the best available value for a locale, falling back to the other
 * language rather than rendering an empty string. Returns null only when
 * neither language has a value.
 */
export function pickBilingual(text, locale) {
    const preferred = locale === LOCALES.AR ? text.ar : text.en;
    const fallback = locale === LOCALES.AR ? text.en : text.ar;
    return preferred ?? fallback ?? null;
}
export const PAGINATION_DEFAULTS = {
    PAGE: 1,
    PAGE_SIZE: 25,
    MAX_PAGE_SIZE: 100,
};
// ---------------------------------------------------------------------------
// Error envelope
// ---------------------------------------------------------------------------
/**
 * Stable machine-readable error codes.
 *
 * The API returns CODES, not user-facing prose. The client renders the
 * translated message from its i18n catalogue, which keeps the API
 * locale-independent and reusable by a future mobile client.
 */
export const ERROR_CODES = {
    // Validation
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
    // Authentication
    NOT_AUTHENTICATED: 'NOT_AUTHENTICATED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
    /**
     * D181: the current password given to change your own was wrong. Answered
     * as 400, never 401 — the SESSION is valid, and a 401 would tell the client
     * to refresh or sign the person out.
     */
    CURRENT_PASSWORD_INCORRECT: 'CURRENT_PASSWORD_INCORRECT',
    /**
     * D180: the administrative "set new password" is not for your own account.
     * Your own is changed from your profile, with your current password, so a
     * session left open is never enough to take an account over.
     */
    SELF_PASSWORD_RESET_NOT_ALLOWED: 'SELF_PASSWORD_RESET_NOT_ALLOWED',
    /**
     * D179/D180: the identity provider refused a password that meets this
     * system's own policy — for example its leaked-password protection. Nothing
     * was changed.
     */
    PASSWORD_REJECTED: 'PASSWORD_REJECTED',
    /**
     * D180: the account has no linked sign-in identity yet (it was provisioned
     * onto an address the identity provider already held; the link is made at
     * first sign-in), so no password can be set for it from here.
     */
    IDENTITY_NOT_LINKED: 'IDENTITY_NOT_LINKED',
    // Authorization
    PERMISSION_DENIED: 'PERMISSION_DENIED',
    /** A user may not review or approve their own work. */
    SELF_REVIEW_NOT_ALLOWED: 'SELF_REVIEW_NOT_ALLOWED',
    SELF_APPROVAL_NOT_ALLOWED: 'SELF_APPROVAL_NOT_ALLOWED',
    /** Phase 12 (D165): whoever completed a corrective action does not verify it. */
    SELF_VERIFICATION_NOT_ALLOWED: 'SELF_VERIFICATION_NOT_ALLOWED',
    // Resources
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    DUPLICATE_RECORD: 'DUPLICATE_RECORD',
    /** Approved results and issued reports are immutable. */
    RECORD_LOCKED: 'RECORD_LOCKED',
    // Domain-specific
    SAMPLE_NOT_ACCEPTED: 'SAMPLE_NOT_ACCEPTED',
    EQUIPMENT_CALIBRATION_EXPIRED: 'EQUIPMENT_CALIBRATION_EXPIRED',
    TEMPLATE_NOT_READY: 'TEMPLATE_NOT_READY',
    REPORT_CANCELLED: 'REPORT_CANCELLED',
    REPORT_SUSPENDED: 'REPORT_SUSPENDED',
    /**
     * A result was selected for a report without being approved, or its approval
     * lock is no longer the one the report recorded. Only approved, locked
     * results may be printed as a laboratory's findings, and the check is made
     * again inside the issuance transaction — approval can be reopened between
     * preparing a report and issuing it.
     */
    RESULT_NOT_APPROVED: 'RESULT_NOT_APPROVED',
    /**
     * Access to the issued file is held. The report itself is untouched: a hold
     * changes no version status, no verification status and the validity of no
     * result (D115). The PUBLIC surface never uses this code, because a client's
     * payment position is not a link-holder's business — there it is
     * ORIGINAL_NOT_AVAILABLE, the same answer as for a laboratory that does not
     * offer the download at all.
     */
    REPORT_ACCESS_HELD: 'REPORT_ACCESS_HELD',
    /**
     * The original cannot be served from the public verification page. It says
     * nothing about WHY — the laboratory may not offer link-holder download, or
     * access may be held — and deliberately nothing about the report's validity,
     * which the page states separately.
     */
    ORIGINAL_NOT_AVAILABLE: 'ORIGINAL_NOT_AVAILABLE',
    DUPLICATE_BILLING: 'DUPLICATE_BILLING',
    /**
     * The laboratory has not switched its client portal on (Phase 11, D146).
     * Answered ONLY after correct portal credentials — before that every
     * failure is the same `INVALID_CREDENTIALS`, so the code never tells a
     * stranger which laboratories offer a portal.
     */
    PORTAL_NOT_AVAILABLE: 'PORTAL_NOT_AVAILABLE',
    /**
     * Phase 12 (D175): an import was not committed because at least one row was
     * rejected. NOTHING was written; the details carry the row report.
     */
    IMPORT_ROWS_REJECTED: 'IMPORT_ROWS_REJECTED',
    // Files
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
    /**
     * The bytes do not match the declared type. Separate from
     * UNSUPPORTED_FILE_TYPE on purpose: "we do not accept this kind of file" and
     * "this file is not what you said it was" are different facts, and the
     * second one is worth being told plainly.
     */
    FILE_CONTENT_MISMATCH: 'FILE_CONTENT_MISMATCH',
    STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',
    /** File storage is not configured on this deployment. */
    STORAGE_NOT_CONFIGURED: 'STORAGE_NOT_CONFIGURED',
    // Throttling and infrastructure
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};
export const DEFAULT_CURRENCY = 'SAR';
/** Minor units per major unit, for currencies used by the platform. */
export const CURRENCY_MINOR_UNITS = Object.freeze({
    SAR: 100,
    USD: 100,
    EUR: 100,
    AED: 100,
    EGP: 100,
    // Zero-decimal currencies
    JPY: 1,
    KWD: 1000,
    BHD: 1000,
    OMR: 1000,
});
export function minorUnitsFor(currency) {
    return CURRENCY_MINOR_UNITS[currency] ?? 100;
}
//# sourceMappingURL=common.js.map